import { type ReactNode, useEffect, useRef, useState } from "react";

import { type CommandFailure, CommandDialog as KitCommandDialog } from "../../ui/command-dialog";
import type { ItineraryFailure } from "../errors";

// The shell of every itinerary command dialog. It is the generic `CommandDialog` of the kit (`src/ui/command-dialog`) drawn with the
// command state of this area (`useItineraryCommandForm`, passed as the kit's `controller`). The rules are the kit's: a failure that cannot work
// again (the plan moved, no access) leaves one button, "Reload the plan", in a dialog titled "This plan changed": the page behind has
// already read the plan again. A failure that may work on a second try locks the fields and says "Try again": the command hook sends
// the same Idempotency-Key for that second try. A refusal that asks for the user's input (a clock gap, a repeated time, a reason) keeps
// the form open so the user can fix it, and an edit of a field clears a refused time.

const FINAL_KINDS: ReadonlySet<ItineraryFailure["kind"]> = new Set([
  "conflict",
  "forbidden",
  "unauthenticated",
  "response",
]);

const isFinalFailure = (failure: ItineraryFailure | null): boolean =>
  failure !== null && !failure.retryable && FINAL_KINDS.has(failure.kind);

/** The failures that ask for something from the user (a changed field, a reason): they read as a warning, not as a fault. */
const ASKS_THE_USER: ReadonlySet<ItineraryFailure["kind"]> = new Set([
  "conflict",
  "needs_reason",
  "time_gap",
  "time_ambiguous",
  "invalid",
  "refused",
]);

/** The state a dialog form needs around one command. `onDone` runs with the outcome of a successful command. */
export function useItineraryCommandForm<T>(onDone: (outcome: T) => void) {
  const [failure, setFailure] = useState<ItineraryFailure | null>(null);
  const [pending, setPending] = useState(false);
  // A completion that arrives after the dialog is gone belongs to a dialog nobody sees: it must not close or navigate a newer one.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function submit(
    run: () => Promise<{ ok: true; outcome: T } | { ok: false; failure: ItineraryFailure }>,
  ) {
    setPending(true);
    setFailure(null);
    const result = await run();
    if (!mounted.current) return;
    setPending(false);
    if (result.ok) onDone(result.outcome);
    else setFailure(result.failure);
  }

  /** The user changed a time field: a refusal about the time that was sent no longer describes the form. */
  const clearTimeFailure = () =>
    setFailure((current) =>
      current !== null && (current.kind === "time_gap" || current.kind === "time_ambiguous")
        ? null
        : current,
    );

  return { failure, pending, submit, terminal: isFinalFailure(failure), clearTimeFailure };
}

type Command = ReturnType<typeof useItineraryCommandForm>;

/** The failure as the kit draws it: the words, the support reference, and whether the form stays open. */
function asKitFailure(failure: ItineraryFailure): CommandFailure {
  return {
    message: failure.message,
    footerNote:
      failure.requestId === null ? undefined : `Reference for support: ${failure.requestId}`,
    retryable: failure.retryable,
    keepForm: !failure.retryable && !FINAL_KINDS.has(failure.kind),
    tone: ASKS_THE_USER.has(failure.kind) ? "conflict" : "error",
    kind: failure.kind,
  };
}

type Props = Readonly<{
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  children?: ReactNode;
  command: Pick<Command, "failure" | "pending" | "clearTimeFailure">;
  /**
   * The plan the page reads now is not the plan this dialog was opened on. Nothing is sent: the dialog says the plan changed and
   * closes on request, so an edit prepared against one version is never sent with the token of another.
   */
  stale?: boolean;
  /** A reason to keep the confirm button off, for example a blocking problem. */
  confirmDisabled?: boolean;
  /** Says why the confirm button is off. */
  confirmDisabledReason?: ReactNode;
  onSubmit: () => void;
  onClose: () => void;
}>;

export function ItineraryCommandDialog({
  title,
  description,
  confirmLabel,
  tone = "primary",
  children,
  command,
  confirmDisabled = false,
  confirmDisabledReason,
  stale = false,
  onSubmit,
  onClose,
}: Props) {
  return (
    <KitCommandDialog
      title={title}
      description={description}
      endedDescription={null}
      confirmLabel={confirmLabel}
      tone={tone}
      closeLabel="Reload the plan"
      staleMessage="This plan changed while this window was open. Nothing was sent. Close this window to read the latest plan, then make the change again."
      titleFor={({ failure, stale: isStale }) =>
        failure?.kind === "conflict" || isStale ? "This plan changed" : undefined
      }
      clearFailureOn="edit"
      stale={stale}
      confirmDisabled={confirmDisabled}
      confirmDisabledReason={confirmDisabledReason}
      controller={{
        failure: command.failure === null ? null : asKitFailure(command.failure),
        pending: command.pending,
        submit: onSubmit,
        clearFailure: command.clearTimeFailure,
      }}
      onClose={onClose}
    >
      {children}
    </KitCommandDialog>
  );
}
